import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Star, Check, Lock, ChevronRight, RotateCcw,
  Info, Heart, Brain, Bone, Wind, Droplets, Eye, Shield, Activity
} from 'lucide-react';

const SYSTEMS = {
  skeletal: { color: '#e2e8f0', label: 'Skeletal', icon: Bone },
  muscular: { color: '#ef4444', label: 'Muscular', icon: Activity },
  circulatory: { color: '#dc2626', label: 'Circulatory', icon: Heart },
  respiratory: { color: '#60a5fa', label: 'Respiratory', icon: Wind },
  digestive: { color: '#f59e0b', label: 'Digestive', icon: Droplets },
  nervous: { color: '#a855f7', label: 'Nervous', icon: Brain },
  endocrine: { color: '#ec4899', label: 'Endocrine', icon: Activity },
  immune: { color: '#10b981', label: 'Immune', icon: Shield },
  urinary: { color: '#06b6d4', label: 'Urinary', icon: Droplets },
  sensory: { color: '#8b5cf6', label: 'Sensory', icon: Eye },
};

const MISSIONS = [
  {
    id: 1, title: 'Skeletal System', difficulty: 1, system: 'skeletal',
    description: 'Click on the bones to discover the 206-bone framework that supports your body!',
    organs: [
      { id: 'skull', name: 'Skull', x: 50, y: 5, w: 12, h: 8, desc: 'Protects the brain. Made of 22 fused bones. The mandible (jaw) is the only movable skull bone.' },
      { id: 'spine', name: 'Spinal Column', x: 48, y: 15, w: 6, h: 25, desc: '33 vertebrae stacked to protect the spinal cord. Allows bending, twisting, and upright posture.' },
      { id: 'ribcage', name: 'Rib Cage', x: 38, y: 15, w: 26, h: 15, desc: '12 pairs of ribs form a cage protecting the heart and lungs. The bottom 2 pairs are "floating ribs".' },
      { id: 'pelvis', name: 'Pelvis', x: 38, y: 38, w: 26, h: 10, desc: 'The hip bone supports the upper body and connects to the legs. It protects the bladder and reproductive organs.' },
      { id: 'femur', name: 'Femur', x: 38, y: 50, w: 10, h: 20, desc: 'The thighbone — the longest, strongest bone in the body. Can support 30x your body weight!' },
      { id: 'humerus', name: 'Humerus', x: 25, y: 18, w: 8, h: 15, desc: 'The upper arm bone connecting shoulder to elbow. "Funny bone" tingles come from the ulnar nerve here.' },
    ],
    fact: 'Babies are born with ~300 bones. Many fuse together as you grow, leaving adults with 206. The smallest bone is the stapes in your ear!',
  },
  {
    id: 2, title: 'Muscular System', difficulty: 1, system: 'muscular',
    description: 'Explore the 600+ muscles that power every movement, from blinking to running!',
    organs: [
      { id: 'biceps', name: 'Biceps', x: 24, y: 20, w: 8, h: 10, desc: 'The arm flexor muscle. Contracts to bend the elbow. Works in pairs with the triceps (antagonistic muscles).' },
      { id: 'pectorals', name: 'Pectorals', x: 38, y: 16, w: 26, h: 8, desc: 'Chest muscles for arm movement and breathing. Push-ups primarily work the pectorals.' },
      { id: 'quadriceps', name: 'Quadriceps', x: 36, y: 48, w: 12, h: 16, desc: 'Four muscles in front of the thigh. The strongest muscle group, essential for walking, running, and jumping.' },
      { id: 'abdominals', name: 'Abdominals', x: 40, y: 28, w: 22, h: 12, desc: 'Core muscles protecting organs and stabilizing the torso. Include the "six-pack" rectus abdominis.' },
      { id: 'deltoids', name: 'Deltoids', x: 25, y: 14, w: 10, h: 6, desc: 'Shoulder muscles that lift and rotate the arm. Named after the Greek letter delta (triangle shape).' },
      { id: 'heart_muscle', name: 'Heart (Cardiac Muscle)', x: 44, y: 18, w: 8, h: 6, desc: 'The only involuntary striated muscle. Beats ~100,000 times per day without conscious effort!' },
    ],
    fact: 'Your tongue is made of 8 muscles. The gluteus maximus (buttock) is the largest muscle. The stapedius in the ear is the smallest!',
  },
  {
    id: 3, title: 'Circulatory System', difficulty: 2, system: 'circulatory',
    description: 'Follow the journey of blood through the heart, arteries, and veins!',
    organs: [
      { id: 'heart', name: 'Heart', x: 43, y: 17, w: 12, h: 10, desc: 'A 4-chambered pump. Right side sends blood to lungs, left side pumps oxygenated blood to the body.' },
      { id: 'aorta', name: 'Aorta', x: 48, y: 13, w: 6, h: 5, desc: 'The largest artery. Carries oxygen-rich blood from the left ventricle to the entire body. Wall is 2mm thick!' },
      { id: 'lungs_circ', name: 'Pulmonary Circuit', x: 34, y: 14, w: 10, h: 12, desc: 'Blood travels from heart → lungs → heart. In the lungs, CO₂ is exchanged for fresh O₂.' },
      { id: 'arteries', name: 'Arteries', x: 30, y: 30, w: 8, h: 20, desc: 'Carry oxygenated blood AWAY from the heart. Thick elastic walls handle high pressure. You feel the pulse here.' },
      { id: 'veins', name: 'Veins', x: 62, y: 30, w: 8, h: 20, desc: 'Carry deoxygenated blood BACK to the heart. Have valves to prevent backflow. Appear blue through skin.' },
      { id: 'capillaries', name: 'Capillaries', x: 42, y: 55, w: 16, h: 6, desc: 'Microscopic vessels (1 cell thick!) where gas exchange happens. If laid end to end, they would circle Earth 2.5 times!' },
    ],
    fact: 'Your blood vessels, laid end-to-end, would stretch 100,000 km — enough to circle Earth 2.5 times! Blood completes a full circuit in about 60 seconds.',
  },
  {
    id: 4, title: 'Respiratory System', difficulty: 1, system: 'respiratory',
    description: 'Breathe in... breathe out! See how your lungs extract oxygen from air.',
    organs: [
      { id: 'nose', name: 'Nasal Cavity', x: 47, y: 3, w: 8, h: 5, desc: 'Filters, warms, and moistens air. Tiny hairs (cilia) trap dust and germs. You breathe ~20,000 times a day!' },
      { id: 'trachea', name: 'Trachea (Windpipe)', x: 47, y: 10, w: 6, h: 6, desc: 'A tube reinforced by C-shaped cartilage rings. Carries air from the throat to the bronchi.' },
      { id: 'bronchi', name: 'Bronchi', x: 40, y: 16, w: 20, h: 5, desc: 'Two branches of the trachea — one to each lung. They subdivide into smaller bronchioles like tree branches.' },
      { id: 'lungs', name: 'Lungs', x: 33, y: 15, w: 34, h: 18, desc: 'Spongy organs filled with 300 million alveoli. Right lung has 3 lobes, left has 2 (to make room for the heart).' },
      { id: 'alveoli', name: 'Alveoli', x: 36, y: 25, w: 10, h: 6, desc: 'Tiny air sacs where O₂ enters blood and CO₂ leaves. Surface area of all alveoli ≈ a tennis court!' },
      { id: 'diaphragm', name: 'Diaphragm', x: 33, y: 34, w: 34, h: 5, desc: 'A dome-shaped muscle below the lungs. Contracts to pull air in (inhale) and relaxes to push air out (exhale).' },
    ],
    fact: 'Your lungs process about 11,000 liters of air per day. The surface area of your alveoli equals about 70 square meters — the size of a tennis court!',
  },
  {
    id: 5, title: 'Digestive System', difficulty: 2, system: 'digestive',
    description: 'Follow your food on its 9-meter journey from mouth to... well, the end!',
    organs: [
      { id: 'mouth', name: 'Mouth', x: 46, y: 5, w: 10, h: 5, desc: 'Digestion starts here! Teeth mechanically break down food. Saliva contains amylase enzyme that starts digesting starch.' },
      { id: 'esophagus', name: 'Esophagus', x: 48, y: 10, w: 5, h: 8, desc: 'A 25cm muscular tube. Peristalsis (wave-like contractions) pushes food to the stomach — even upside down!' },
      { id: 'stomach', name: 'Stomach', x: 42, y: 22, w: 16, h: 10, desc: 'A J-shaped bag that churns food with HCl acid (pH 1.5-3.5). Can hold about 1 liter of food. Digestion takes 2-5 hours.' },
      { id: 'liver', name: 'Liver', x: 36, y: 18, w: 12, h: 8, desc: 'The largest internal organ (1.5 kg!). Produces bile for fat digestion, detoxifies blood, stores vitamins, and has 500+ functions.' },
      { id: 'small_intestine', name: 'Small Intestine', x: 38, y: 33, w: 24, h: 12, desc: '6 meters long with villi (finger-like projections) that absorb nutrients. 90% of digestion and absorption happens here!' },
      { id: 'large_intestine', name: 'Large Intestine', x: 35, y: 42, w: 30, h: 14, desc: '1.5m long. Absorbs water and minerals. Houses trillions of gut bacteria that help with digestion and immunity.' },
    ],
    fact: 'Your small intestine is about 6 meters long. If you flattened all its villi, the surface area would cover a tennis court! Digestion takes 24-72 hours total.',
  },
  {
    id: 6, title: 'Nervous System', difficulty: 2, system: 'nervous',
    description: 'The body command center — brain, spinal cord, and billions of neurons!',
    organs: [
      { id: 'brain', name: 'Brain', x: 42, y: 2, w: 16, h: 10, desc: 'Contains ~86 billion neurons. Uses 20% of body energy. Has lobes: frontal (thinking), temporal (hearing), occipital (vision), parietal (touch).' },
      { id: 'cerebellum', name: 'Cerebellum', x: 52, y: 8, w: 8, h: 5, desc: 'The "little brain" at the back. Controls balance, coordination, and motor learning. Contains more neurons than the rest of the brain combined!' },
      { id: 'spinal_cord', name: 'Spinal Cord', x: 48, y: 12, w: 4, h: 28, desc: 'Information highway between brain and body. Protected by vertebrae. Reflex arcs bypass the brain for faster responses.' },
      { id: 'peripheral', name: 'Peripheral Nerves', x: 25, y: 20, w: 8, h: 30, desc: 'A network of nerves branching from the spinal cord to every body part. Sensory nerves bring info IN, motor nerves send commands OUT.' },
      { id: 'synapse', name: 'Synapses', x: 60, y: 25, w: 10, h: 8, desc: 'Gaps between neurons where neurotransmitters carry signals. Your brain has ~100 trillion synapses — more than stars in the Milky Way!' },
      { id: 'autonomic', name: 'Autonomic System', x: 42, y: 38, w: 16, h: 6, desc: 'Controls involuntary functions: heartbeat, digestion, breathing. Has sympathetic (fight/flight) and parasympathetic (rest/digest) divisions.' },
    ],
    fact: 'Nerve signals travel at up to 120 m/s — fast enough to reach your toes in 0.02 seconds! Your brain generates about 12-25 watts of electricity.',
  },
  {
    id: 7, title: 'Endocrine System', difficulty: 2, system: 'endocrine',
    description: 'Discover the glands that produce hormones — your body chemical messengers!',
    organs: [
      { id: 'pituitary', name: 'Pituitary Gland', x: 47, y: 6, w: 8, h: 4, desc: 'The "master gland" (pea-sized!). Controls growth, metabolism, and other glands. Produces growth hormone and many more.' },
      { id: 'thyroid', name: 'Thyroid', x: 44, y: 12, w: 12, h: 4, desc: 'Butterfly-shaped gland in the neck. Controls metabolism rate. Too much = hyperactive, too little = sluggish.' },
      { id: 'adrenal', name: 'Adrenal Glands', x: 40, y: 25, w: 20, h: 5, desc: 'Sit atop the kidneys. Produce adrenaline (fight/flight), cortisol (stress), and aldosterone (blood pressure).' },
      { id: 'pancreas', name: 'Pancreas', x: 42, y: 30, w: 16, h: 5, desc: 'Dual function: produces digestive enzymes AND insulin/glucagon hormones that control blood sugar levels.' },
      { id: 'hypothalamus', name: 'Hypothalamus', x: 44, y: 3, w: 14, h: 4, desc: 'Links the nervous system to the endocrine system. Controls body temperature, hunger, thirst, sleep, and emotions.' },
      { id: 'pineal', name: 'Pineal Gland', x: 52, y: 4, w: 6, h: 3, desc: 'Produces melatonin, which regulates sleep-wake cycles. Light inhibits melatonin — that is why screens before bed disrupt sleep!' },
    ],
    fact: 'Hormones are so powerful that just a tiny amount (nanograms) can cause major body changes. Adrenaline can increase your heart rate and strength in seconds!',
  },
  {
    id: 8, title: 'Immune System', difficulty: 2, system: 'immune',
    description: 'Your body defense army — white blood cells, antibodies, and more!',
    organs: [
      { id: 'wbc', name: 'White Blood Cells', x: 42, y: 20, w: 16, h: 8, desc: 'Your immune soldiers. Types include neutrophils (first responders), lymphocytes (T & B cells), and macrophages (big eaters).' },
      { id: 'thymus', name: 'Thymus', x: 44, y: 14, w: 12, h: 5, desc: 'Trains T-cells to recognize threats. Most active in childhood, then gradually shrinks. Located behind the breastbone.' },
      { id: 'spleen', name: 'Spleen', x: 55, y: 25, w: 10, h: 7, desc: 'Filters blood, removes old red blood cells, and stores white blood cells. You can live without it, but get sick more easily.' },
      { id: 'lymph', name: 'Lymph Nodes', x: 30, y: 15, w: 8, h: 25, desc: 'Small bean-shaped filters throughout the body. They trap pathogens and activate immune cells. Swollen nodes = immune system fighting!' },
      { id: 'bone_marrow', name: 'Bone Marrow', x: 38, y: 50, w: 12, h: 8, desc: 'The factory! Red marrow produces all blood cells — red, white, and platelets. About 200 billion new cells are made daily!' },
      { id: 'antibodies', name: 'Antibodies', x: 58, y: 35, w: 10, h: 6, desc: 'Y-shaped proteins made by B-cells. Each antibody matches a specific antigen (like a lock and key). Vaccines train your body to make them!' },
    ],
    fact: 'Your body makes about 1 billion new white blood cells every day. Memory B-cells can remember a pathogen for decades — that is how vaccines provide long-term protection!',
  },
  {
    id: 9, title: 'Urinary System', difficulty: 1, system: 'urinary',
    description: 'The filtration plant — kidneys filter 180 liters of blood daily!',
    organs: [
      { id: 'kidneys', name: 'Kidneys', x: 38, y: 25, w: 24, h: 10, desc: 'Two bean-shaped organs containing 1 million nephrons each. Filter blood, remove waste, balance electrolytes and water.' },
      { id: 'nephron', name: 'Nephrons', x: 55, y: 22, w: 10, h: 6, desc: 'Microscopic filtering units. Each has a glomerulus (filter) and tubule (reabsorption). You filter 180L/day but only excrete ~1.5L!' },
      { id: 'ureter', name: 'Ureters', x: 42, y: 35, w: 16, h: 8, desc: 'Two muscular tubes (25-30cm) that carry urine from kidneys to bladder using peristalsis (wave-like contractions).' },
      { id: 'bladder', name: 'Bladder', x: 42, y: 45, w: 16, h: 8, desc: 'A stretchy muscular sac that stores urine. Can hold 400-600ml. Stretch receptors signal when it is time to go!' },
      { id: 'urethra', name: 'Urethra', x: 47, y: 54, w: 6, h: 6, desc: 'The exit tube from bladder to outside. Sphincter muscles control release. Shorter in females than males.' },
      { id: 'adrenal_u', name: 'Adrenal Glands', x: 38, y: 22, w: 24, h: 4, desc: 'Sit on top of kidneys. Produce hormones including aldosterone which tells kidneys how much sodium and water to keep.' },
    ],
    fact: 'Your kidneys filter ALL your blood about 40 times a day. They receive 20-25% of cardiac output despite being only 0.5% of body weight!',
  },
  {
    id: 10, title: 'Sensory Organs', difficulty: 2, system: 'sensory',
    description: 'The five senses — how your body perceives the world around you!',
    organs: [
      { id: 'eye', name: 'Eye (Vision)', x: 43, y: 4, w: 7, h: 4, desc: 'Contains 120 million rod cells (light/dark) and 6 million cone cells (color). Can distinguish 10 million colors! Blinks 15-20 times/min.' },
      { id: 'ear', name: 'Ear (Hearing)', x: 35, y: 5, w: 6, h: 5, desc: 'Sound waves vibrate the eardrum → ossicles → cochlea. Humans hear 20Hz-20kHz. The cochlea has 15,000 hair cells.' },
      { id: 'nose_sense', name: 'Nose (Smell)', x: 48, y: 5, w: 6, h: 4, desc: '400 types of smell receptors detect over 1 trillion different scents. Smell is strongly linked to memory and emotion.' },
      { id: 'tongue', name: 'Tongue (Taste)', x: 47, y: 8, w: 8, h: 3, desc: '~10,000 taste buds detect 5 basic tastes: sweet, salty, sour, bitter, and umami. Taste buds regenerate every 10-14 days!' },
      { id: 'skin', name: 'Skin (Touch)', x: 25, y: 30, w: 10, h: 15, desc: 'The largest organ (2 m²). Contains receptors for pressure, temperature, pain, and vibration. Fingertips have the most nerve endings.' },
      { id: 'proprioception', name: 'Proprioception', x: 60, y: 40, w: 12, h: 8, desc: 'The "sixth sense" — knowing where your body parts are without looking. Receptors in muscles, joints, and tendons provide position info.' },
    ],
    fact: 'Your eyes can detect a single photon of light! Your nose can detect certain chemicals at concentrations as low as a few parts per trillion.',
  },
];

const BODY_IMAGES = {
  skeleton: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/f477bfbcf9628f2837adc3abad145a2dbbceca930df61578950b7e9cc9fec7f7.png',
  organs: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/16436b114f944fc253ebf88e10c5933dd0605fd28541804dd070a2a8304df958.png',
  muscles: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/929652e16965de717d3e55e07d3874118606be5f849f8987af6bba799e82f10d.png',
  nervous: 'https://static.prod-images.emergentagent.com/jobs/f072039a-19c4-425b-b071-367212e38fcd/images/33b7efa9492ea68afce7a1b113b3abcad7ddddf42b7c190312bb6b9802708cc4.png',
};

const SYSTEM_IMAGE_MAP = {
  skeletal: 'skeleton', muscular: 'muscles', circulatory: 'nervous',
  respiratory: 'organs', digestive: 'organs', nervous: 'nervous',
  endocrine: 'organs', immune: 'organs', urinary: 'organs', sensory: 'organs',
};

export default function BodyExplorerLab() {
  const navigate = useNavigate();
  const [showMissions, setShowMissions] = useState(true);
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('body-explorer', 10);
  const [score, setScore] = useState(0);
  const [showFact, setShowFact] = useState(false);
  const [discoveredOrgans, setDiscoveredOrgans] = useState(new Set());
  const [selectedOrgan, setSelectedOrgan] = useState(null);

  const mission = MISSIONS[currentMission];
  const sys = SYSTEMS[mission.system];

  const handleOrganClick = (organ) => {
    setSelectedOrgan(organ);
    setDiscoveredOrgans(prev => {
      const next = new Set(prev);
      next.add(organ.id);
      return next;
    });
  };

  const allDiscovered = mission.organs.every(o => discoveredOrgans.has(o.id));

  const completeMission = () => {
    if (!completedMissions.includes(currentMission)) {
      saveProgress(currentMission);
      setScore(s => s + mission.difficulty * 10);
    }
    setShowFact(true);
  };

  const resetMission = () => {
    setDiscoveredOrgans(new Set());
    setSelectedOrgan(null);
    setShowFact(false);
  };

  if (showMissions) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/labs')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <h1 className="text-lg font-bold flex items-center gap-2"><Heart className="w-5 h-5 text-rose-400" /> Human Body Explorer</h1>
            <Badge variant="secondary" className="bg-rose-500/20 text-rose-300">{score} pts</Badge>
          </div>
        </header>
        <main className="max-w-6xl mx-auto p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold mb-1">Choose a Body System</h2>
            <p className="text-sm text-muted-foreground">Explore the amazing human body — click organs to learn!</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="mission-grid">
            {MISSIONS.map((m, idx) => {
              const locked = !unlockedMissions.includes(idx);
              const done = completedMissions.includes(idx);
              const s = SYSTEMS[m.system];
              return (
                <motion.button key={m.id} whileHover={!locked ? { scale: 1.04 } : {}} whileTap={!locked ? { scale: 0.97 } : {}}
                  onClick={() => { if (!locked) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                  disabled={locked}
                  className={`relative p-4 rounded-2xl border text-left transition-all ${
                    done ? 'border-emerald-500/30 bg-emerald-500/5' :
                    locked ? 'border-border bg-card opacity-70 cursor-not-allowed' :
                    'border-border bg-card hover:bg-muted/50'
                  }`}
                  style={!locked && !done ? { borderColor: s.color + '30' } : {}}
                  data-testid={`mission-${idx}`}>
                  {locked && <Lock className="absolute top-3 right-3 w-3 h-3 text-muted-foreground/30" />}
                  {done && <Check className="absolute top-3 right-3 w-4 h-4 text-emerald-400" />}
                  <span className="text-[10px] text-foreground uppercase tracking-widest">System {idx + 1}</span>
                  <p className="text-sm font-semibold mt-1 text-foreground leading-tight">{m.title}</p>
                  <div className="flex items-center gap-0.5 mt-2">
                    {[...Array(3)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < m.difficulty ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/20'}`} />)}
                  </div>
                </motion.button>
              );
            })}
          </div>
        </main>
      </div>
    );
  }

  const isCompleted = completedMissions.includes(currentMission);
  const IconComp = sys.icon;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => setShowMissions(true)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition" data-testid="back-to-missions">
            <ArrowLeft className="w-4 h-4" /> Missions
          </button>
          <h1 className="text-sm font-semibold text-foreground/80">{mission.title}</h1>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground/70">{score} pts</span>
            <span className="text-xs text-muted-foreground/70">{completedMissions.length}/10</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4" data-testid="body-explorer">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_350px] gap-6">
          {/* Body Diagram */}
          <div className="space-y-4">
            <div className="relative rounded-2xl border border-border overflow-hidden" data-testid="body-diagram"
              style={{ minHeight: '500px', background: '#060a14' }}>
              {/* Real human body image - FULL VISIBILITY */}
              <img
                src={BODY_IMAGES[SYSTEM_IMAGE_MAP[mission.system] || 'organs']}
                alt={`${mission.title} anatomy`}
                className="w-full object-contain mx-auto"
                style={{ maxHeight: '500px', display: 'block' }}
                loading="eager"
              />

              {/* Clickable organ spots - small circles, NOT rectangles */}
              {mission.organs.map(organ => {
                const discovered = discoveredOrgans.has(organ.id);
                const selected = selectedOrgan?.id === organ.id;
                // Place dot at center of old rectangle area
                const dotX = organ.x + (organ.w || 0) / 2;
                const dotY = organ.y + (organ.h || 0) / 2;
                return (
                  <motion.button key={organ.id}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => handleOrganClick(organ)}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                    style={{ left: `${dotX}%`, top: `${dotY}%` }}
                    data-testid={`organ-${organ.id}`}>
                    {discovered ? (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}
                        className={`px-2.5 py-1 rounded-lg border-2 bg-black/80 backdrop-blur-sm whitespace-nowrap shadow-lg ${selected ? 'ring-2 ring-offset-1 ring-offset-black' : ''}`}
                        style={{ borderColor: sys.color, ringColor: sys.color }}>
                        <span className="text-[11px] font-bold" style={{ color: sys.color }}>{organ.name}</span>
                      </motion.div>
                    ) : (
                      <motion.div
                        animate={{ scale: [1, 1.3, 1], boxShadow: [`0 0 0 0 ${sys.color}40`, `0 0 0 10px ${sys.color}00`, `0 0 0 0 ${sys.color}40`] }}
                        transition={{ repeat: Infinity, duration: 2 }}
                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center cursor-pointer ${selected ? 'ring-2 ring-white' : ''}`}
                        style={{ backgroundColor: sys.color + '25', borderColor: sys.color + '80' }}>
                        <span className="text-[9px] font-bold" style={{ color: sys.color + 'cc' }}>?</span>
                      </motion.div>
                    )}
                  </motion.button>
                );
              })}

              {/* Discovery count */}
              <div className="absolute bottom-3 left-3 text-xs text-muted-foreground/70">
                Discovered: {discoveredOrgans.size}/{mission.organs.length}
              </div>
            </div>

            {/* Selected Organ Detail */}
            <AnimatePresence>
              {selectedOrgan && (
                <motion.div key={selectedOrgan.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="p-4 rounded-2xl border bg-card" data-testid="organ-info"
                  style={{ borderColor: sys.color + '30' }}>
                  <h3 className="font-bold text-base flex items-center gap-2" style={{ color: sys.color }}>
                    <IconComp className="w-4 h-4" /> {selectedOrgan.name}
                  </h3>
                  <p className="text-sm text-foreground/80 mt-2 leading-relaxed">{selectedOrgan.desc}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Actions */}
            <div className="flex gap-3">
              {allDiscovered && !isCompleted && (
                <Button onClick={completeMission} className="bg-gradient-to-r from-emerald-500 to-teal-600" data-testid="complete-btn">
                  <Check className="w-4 h-4 mr-1" /> All Organs Found!
                </Button>
              )}
              <Button variant="outline" onClick={resetMission} className="border-white/20 text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
            </div>

            <AnimatePresence>
              {showFact && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl border bg-card" data-testid="body-fact"
                  style={{ borderColor: sys.color + '20', backgroundColor: sys.color + '08' }}>
                  <p className="text-xs font-semibold flex items-center gap-1 mb-1" style={{ color: sys.color }}><Info className="w-3 h-3" /> Did You Know?</p>
                  <p className="text-sm text-foreground/80">{mission.fact}</p>
                  {currentMission < MISSIONS.length - 1 && (
                    <Button size="sm" onClick={() => { setCurrentMission(p => p + 1); resetMission(); }}
                      className="mt-3" style={{ backgroundColor: sys.color }} data-testid="next-mission-btn">
                      Next System <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 lg:sticky lg:top-[65px] lg:self-start">
            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5">
                <IconComp className="w-4 h-4" style={{ color: sys.color }} /> {sys.label} System
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{mission.description}</p>
              <p className="text-xs text-muted-foreground/60 mt-2">Click the pulsing spots on the body diagram to discover each organ!</p>
            </div>

            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Organs to Find</h3>
              <div className="space-y-1.5">
                {mission.organs.map(o => (
                  <div key={o.id} onClick={() => handleOrganClick(o)}
                    className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer transition-all ${
                      discoveredOrgans.has(o.id) ? 'text-foreground/80 bg-muted/50' : 'text-muted-foreground/60 hover:text-muted-foreground'
                    }`}>
                    <span className={`w-4 h-4 rounded flex items-center justify-center text-[10px] ${
                      discoveredOrgans.has(o.id) ? 'bg-emerald-500 text-white' : 'bg-muted/50'
                    }`}>
                      {discoveredOrgans.has(o.id) ? <Check className="w-3 h-3" /> : '?'}
                    </span>
                    {discoveredOrgans.has(o.id) ? o.name : '???'}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 rounded-2xl border border-border bg-card">
              <h3 className="font-semibold text-sm mb-2">Progress</h3>
              <div className="space-y-1.5">
                {MISSIONS.map((m, idx) => (
                  <button key={m.id} onClick={() => { if (unlockedMissions.includes(idx)) { setCurrentMission(idx); setShowMissions(false); resetMission(); } }}
                    disabled={!unlockedMissions.includes(idx)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-all ${
                      idx === currentMission ? 'bg-primary text-primary-foreground' :
                      completedMissions.includes(idx) ? 'text-emerald-400 hover:bg-muted/50' :
                      unlockedMissions.includes(idx) ? 'text-muted-foreground hover:bg-muted/50' : 'text-muted-foreground/30 cursor-not-allowed'
                    }`}>
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold ${
                      completedMissions.includes(idx) ? 'bg-emerald-500 text-white' :
                      idx === currentMission ? 'bg-primary text-primary-foreground' : 'bg-muted/50'
                    }`}>
                      {completedMissions.includes(idx) ? <Check className="w-3 h-3" /> : unlockedMissions.includes(idx) ? idx + 1 : <Lock className="w-2.5 h-2.5" />}
                    </span>
                    <span className="truncate">{m.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
